import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * AdminShell — full-width console layout for the platform admin area.
 *
 * Top bar: navy gradient with REGARA logo + "Platform Admin" label on the left,
 * and an optional `actions` slot on the right (logout button, etc.).
 *
 * NAV: seven of the thirteen destinations were per-agent run tables, and listing
 * them all flat overflowed the bar — items wrapped onto two lines and "Settings"
 * was clipped off the right edge at common window widths. The run tables now sit
 * behind one "Agent Runs" menu, which keeps the bar to six items on one line and
 * groups pages that genuinely belong together. Routes are unchanged, so existing
 * links and bookmarks still work.
 *
 * Below: light page background with a single max-width content container
 * (`max-w-[1280px]`) shared by every admin tab, so the console width stays
 * uniform across Users, Companies, and Terms.
 *
 * Intentionally chrome-less (no main navbar) since admin paths are internal-only.
 */

interface AdminShellProps {
  actions?: ReactNode;
  children: ReactNode;
}

const NAV_BASE =
  'text-[13px] font-semibold tracking-[0.02em] pb-1.5 border-b-2 border-transparent transition-colors whitespace-nowrap';
const NAV_INACTIVE = 'text-white/70 hover:text-white';
const NAV_ACTIVE = 'text-white border-white';

/** The per-agent run tables, grouped behind one menu. */
const RUN_LINKS = [
  { to: '/admin/dossier-runs', label: 'Dossier' },
  { to: '/admin/predicate-runs', label: 'Predicate' },
  { to: '/admin/strategy-runs', label: 'Strategy' },
  { to: '/admin/extraction-runs', label: 'Extraction' },
  { to: '/admin/archive-import-runs', label: 'Archive Import' },
  { to: '/admin/clinical-protocol-runs', label: 'Clinical Protocol' },
  { to: '/admin/clinical-report-runs', label: 'Clinical Report' },
] as const;

/** Top-level destinations, in the order an operator reaches for them. */
const TOP_LINKS = [
  { to: '/admin/dashboard', label: 'Users' },
  { to: '/admin/companies', label: 'Companies' },
  { to: '/admin/terms', label: 'Terms' },
] as const;

const TAIL_LINKS = [
  { to: '/admin/mcp-health', label: 'MCP Health' },
  { to: '/admin/vault-uploads', label: 'Vault Uploads' },
  { to: '/admin/settings', label: 'Settings' },
] as const;

export function AdminShell({ actions, children }: AdminShellProps) {
  // The trigger has to show the active underline when ANY run table is open —
  // it stands in for those links, and without this the bar looks like nothing
  // is selected while you are plainly on a run page.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onRunsPage = RUN_LINKS.some((l) => pathname.startsWith(l.to));
  const currentLabel =
    [...TOP_LINKS, ...TAIL_LINKS].find((l) => pathname.startsWith(l.to))?.label ??
    RUN_LINKS.find((l) => pathname.startsWith(l.to))?.label ??
    'Menu';

  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      {/* ── Top bar ── */}
      <header
        className="w-full"
        style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        }}
      >
        <div className="px-6 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/regara_logo_1.png" className="h-7 w-auto" alt="REGARA" />
            <span className="text-white text-[11px] font-semibold uppercase tracking-[0.18em] opacity-80 hidden sm:inline">
              Platform Admin
            </span>
          </div>

          {actions && <div className="flex items-center gap-3">{actions}</div>}
        </div>

        {/* ── Nav ──
            Wide: the six top-level items inline, run tables behind one menu.
            Narrow: ONE menu holding everything. Horizontal scrolling was not enough —
            Settings sat off-screen with no sign it existed, which is exactly the kind
            of hidden destination people never find. The trigger names the current
            section so the bar still answers "where am I". */}
        <nav className="px-6 sm:px-8" aria-label="Admin sections">
          {/* Compact */}
          <div className="lg:hidden pb-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  className="inline-flex items-center gap-2 text-white text-[13px] font-semibold tracking-[0.02em] bg-white/10 hover:bg-white/15 rounded-lg px-3 py-1.5 transition-colors cursor-pointer"
                >
                  <Menu className="size-4" aria-hidden />
                  {currentLabel}
                  <ChevronDown className="size-3.5 opacity-70" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                {TOP_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} className="w-full cursor-pointer">{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-muted">
                  Agent Runs
                </DropdownMenuLabel>
                {RUN_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} className="w-full cursor-pointer">{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {TAIL_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} className="w-full cursor-pointer">{l.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Wide */}
          <div className="hidden lg:flex items-center gap-6">
            {TOP_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={NAV_BASE}
                activeProps={{ className: `${NAV_BASE} ${NAV_ACTIVE}` }}
                inactiveProps={{ className: `${NAV_BASE} ${NAV_INACTIVE}` }}
              >
                {l.label}
              </Link>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  className={cn(
                    NAV_BASE,
                    'inline-flex items-center gap-1 cursor-pointer bg-transparent',
                    onRunsPage ? NAV_ACTIVE : NAV_INACTIVE,
                  )}
                >
                  Agent Runs
                  <ChevronDown className="size-3.5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {RUN_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} className="w-full cursor-pointer">{l.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {TAIL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={NAV_BASE}
                activeProps={{ className: `${NAV_BASE} ${NAV_ACTIVE}` }}
                inactiveProps={{ className: `${NAV_BASE} ${NAV_INACTIVE}` }}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {/* ── Content ── */}
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
